import { miraRoutes, routeById, type MiraMainTabId } from '@mira/clients-shared';
import { useEffect, useMemo, useState } from 'react';
import { createWebApiClient, hasStoredToken } from '../api/webSession.js';
import { readRouteState, type WebRouteState } from '../app/route-state.js';
import { navigateTo } from '../app/router.js';
import { ScreenRouter } from '../screens/ScreenRouter.js';
import { BottomTabs } from './BottomTabs.js';
import { SheetHost } from './SheetHost.js';
import { ToastHost, type ToastMessage } from './ToastHost.js';

export type AuthStatus = 'checking' | 'guest' | 'authed' | 'failed';

export function AppShell() {
  const [route, setRoute] = useState<WebRouteState>(() => readRouteState());
  const [authStatus, setAuthStatus] = useState<AuthStatus>('checking');
  const [authRetryKey, setAuthRetryKey] = useState(0);
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  useEffect(() => {
    const onRoute = () => setRoute(readRouteState());
    window.addEventListener('hashchange', onRoute);
    window.addEventListener('popstate', onRoute);
    return () => {
      window.removeEventListener('hashchange', onRoute);
      window.removeEventListener('popstate', onRoute);
    };
  }, []);

  useEffect(() => {
    const onToast = (event: Event) => {
      const detail = (event as CustomEvent<Partial<ToastMessage>>).detail;
      if (!detail?.text) return;
      const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const toast = { id, text: detail.text || '', tone: detail.tone };
      setToasts((items) => [...items.filter((item) => item.text !== toast.text || item.tone !== toast.tone).slice(-2), toast]);
      window.setTimeout(() => setToasts((items) => items.filter((item) => item.id !== id)), 3200);
    };
    window.addEventListener('mira:toast', onToast);
    return () => window.removeEventListener('mira:toast', onToast);
  }, []);

  useEffect(() => {
    requestAnimationFrame(() => {
      document.querySelector('.mira-phone-frame > article')?.scrollTo({ top: 0 });
    });
  }, [route.path]);

  useEffect(() => {
    let active = true;
    if (!hasStoredToken()) {
      setAuthStatus(hasStoredToken() ? 'authed' : 'guest');
      if (route.routeId === 'startup') {
        navigateTo('login');
      }
      return;
    }
    setAuthStatus('checking');
    createWebApiClient().me()
      .then((me) => {
        if (!active) return;
        setAuthStatus('authed');
        if (me.onboarding?.completed === false && !route.routeId.startsWith('onboarding')) {
          navigateTo('onboarding-profile');
        } else if (route.routeId === 'startup') {
          navigateTo('today');
        }
      })
      .catch(() => {
        if (active) setAuthStatus('failed');
      });
    return () => {
      active = false;
    };
  }, [route.routeId, authRetryKey]);

  const routeConfig = routeById(route.routeId);
  const activeTab = useMemo<MiraMainTabId | undefined>(() => {
    return miraRoutes.find((item) => item.id === route.routeId)?.mainTab;
  }, [route.routeId]);
  const fullScreen = routeConfig.fullScreen || routeConfig.kind === 'chat';
  const showBottomTabs = routeConfig.kind === 'main-tab';

  return (
    <main className="mira-app-shell" data-fullscreen={fullScreen || undefined}>
      <section className="mira-phone-frame" aria-label="Mira Web">
        <ScreenRouter authStatus={authStatus} onRetryAuth={() => setAuthRetryKey((value) => value + 1)} route={route} />
        {showBottomTabs && !fullScreen ? <BottomTabs active={activeTab} /> : null}
      </section>
      <SheetHost />
      <ToastHost messages={toasts} />
    </main>
  );
}
